<template>
    <div
        class="loyalty-program col-span-12 md:col-span-6 lg:col-span-4 mb-5 relative overflow-visible rounded-lg bg-white px-4 pt-5 pb-12 shadow sm:px-6 sm:pt-6 border border-gray-200"
        :class="{
            'no-connected-account-rewards-sync': program.has_rewards_filter,
        }"
    >
        <div
            class="absolute inset-0 flex flex-col justify-center items-center bg-white bg-opacity-90 z-40 px-6"
            v-if="program.has_rewards_filter"
        >
            <div class="flex items-center flex-col">
                <img
                    :src="`${cdnUrl}/${program.program_image.path}/_small/${program.program_image.file_name}`"
                    alt="Program"
                    class="object-contain mb-2"
                    style="max-height: 50px"
                />
                <h5 class="font-bold text-lg mb-2">{{ program.name }}</h5>
            </div>
            <p class="text-center text-xs font-medium text-gray-500 mb-2">
                Unfortunately {{ program.bank.name }} doesn't sync rewards for
                this program. You will have to manually track your
                {{ program.name }} points balance.
            </p>
            <button
                class="btn btn-primary btn-sm"
                @click="convertToManualProgram"
            >
                I understand
                <i class="fa-solid fa-thumbs-up ml-2 fa-lg text-orange"></i>
            </button>
        </div>

        <div
            class="absolute inset-0 flex flex-col justify-center items-center bg-gray-100 bg-opacity-95 z-40 px-6"
            v-if="
                program.program_user && !program.program_user.converted_notified
            "
        >
            <div class="flex items-center flex-col">
                <img
                    :src="`${cdnUrl}/${program.program_image.path}/_small/${program.program_image.file_name}`"
                    class="object-contain mb-3"
                    style="max-height: 40px"
                />
                <h5 class="font-bold text-lg mb-2 sr-only">
                    {{ program.name }}
                </h5>
            </div>
            <p class="text-center text-xs font-medium text-gray-500 mb-2">
                We've created this rewards program for you since
                {{ program.bank.name }} does not sync your rewards balance with
                My10x.
            </p>
            <p class="text-center text-xs font-medium text-gray-500 mb-2">
                You will have to
                <span class="font-bold">manually update the rewards.</span>
            </p>

            <button
                class="rounded bg-white py-1 px-2 text-sm font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                @click="setConvertedNotified"
            >
                Ok got it
                <i class="fa-lg fa-solid fa-thumbs-up ml-1"></i>
            </button>
        </div>

        <div class="flex flex-col justify-between h-full">
            <div class="flex w-full flex-wrap mb-2 pb-12">
                <div class="program-logo w-24 flex-initial mr-3">
                    <img
                        :src="`${cdnUrl}/${program.program_image.path}/_small/${program.program_image.file_name}`"
                        class="object-contain"
                        style="max-height: 60px"
                    />
                </div>
                <div class="program-name flex-1">
                    <p class="font-bold text-orange leading-none text-2xl mb-1">
                        {{ $filters.formatThousand(program.points) }}
                    </p>
                    <h3
                        class="text-sm font-medium text-gray-500 leading-tight mb-1"
                    >
                        {{ program.name }}
                    </h3>
                </div>
                <ProgramDropdown :program="program" :user="user" />

                <ProgramStatus :program="program" />
            </div>
            <div
                class="flex w-full items-end justify-between px-2 absolute inset-x-0 bottom-0 bg-gray-50 py-4"
            >
                <div class="program-type">
                    <p class="text-sm font-medium text-gray-900">
                        {{ programName(program.type) }}
                    </p>

                    <p class="text-xs text-gray-500">
                        Last updated on {{ program.last_updated_at }}
                    </p>
                </div>
                <div v-if="program.connected && !program.use_manual_points">
                    <div
                        class="flex items-center justify-center rounded-full bg-gray-200 !text-gray-600 text-xs w-6 h-6 relative annual-fee-label-trigger cursor-pointer ml-2 z-30"
                    >
                        <span class="annual-fee-label text-xs">
                            Your {{ program.name }} account syncs your rewards
                            automatically.
                        </span>
                        <i
                            class="fa-regular fa-link-simple !text-gray-600 fa-sm"
                        ></i>
                    </div>
                </div>
                <div v-if="program.connected && program.use_manual_points">
                    <div
                        class="flex items-center justify-center rounded-full bg-green-200 !text-green-600 text-xs w-6 h-6 relative annual-fee-label-trigger cursor-pointer ml-2 z-30"
                    >
                        <span class="annual-fee-label text-xs">
                            Your {{ program.name }} account is available to sync
                            your rewards automatically. However, you are
                            currently managing this program.
                        </span>
                        <i
                            class="fa-regular fa-arrows-left-right !text-green-600 fa-sm"
                        ></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { usePointsStore } from '@/store/points';

import vClickOutside from 'click-outside-vue3';
import ProgramDropdown from '@/components/mypoints/program-dropdown.vue';
import ProgramStatus from '@/components/mypoints/program-status.vue';

export default {
    components: {
        ProgramDropdown,
        ProgramStatus,
    },
    directives: {
        ClickOutside: vClickOutside.directive,
    },
    props: {
        program: Object,
        user: Object,
    },
    data: () => ({
        errors: [],
        loading: false,
        programFilters: [
            { label: 'Airline', value: 'airline' },
            { label: 'Cashback', value: 'cashback' },
            { label: 'Hotels', value: 'hotel' },
            { label: 'Flexible Currency', value: 'flexible_currency' },
        ],
    }),
    computed: {
        cdnUrl() {
            return window.cdnUrl;
        },
    },
    methods: {
        ...mapActions(usePointsStore, ['getRewards']),
        programName(type) {
            let program = this.programFilters.find((program) => {
                return type === program.value;
            });
            return program ? program.label : '';
        },
        convertToManualProgram() {
            this.saveConvertBtnAction = true;
            ApiService.apiCall('/programs/convert-manual', 'post', {
                program: this.program,
                user: this.user,
            })
                .then((response) => {
                    this.$notify(
                        {
                            group: 'success',
                            title: 'Success',
                            text: 'Your program has been converted to a manual program.',
                        },
                        4000
                    );
                    this.saveConvertBtnAction = false;
                    this.getRewards();
                })
                .catch((error) => {
                    this.$root.handleHttpStatus(error);
                    this.saveConvertBtnAction = false;
                });
        },
        setConvertedNotified() {
            ApiService.apiCall('/programs/converted-notified', 'post', {
                program_id: this.program.id,
                user_id: this.user.id,
                wallet_id: this.program.program_user.wallet_id,
            })
                .then((response) => {
                    this.getRewards();
                })
                .catch((error) => {
                    this.$root.handleHttpStatus(error);
                });
        },
    },
};
</script>
<style lang="scss" scoped>
.annual-fee-label-trigger {
    &:hover {
        .annual-fee-label {
            display: block;
        }
    }
}
.annual-fee-label {
    display: none;
    width: 200px;
    padding: 6px 12px;
    color: #fff;
    background-color: #1c293d;
    border-radius: 4px;
    position: absolute;
    bottom: 110%;
    left: 50%;
    margin-left: -100px;
}
</style>
